import type { Query, QueryClient } from "@-/com.npmjs.tanstack__react-query";

export interface InactiveQueryMemoryBudgetOptions {
  /** Time for a completed prefetch/read to be adopted by a mounted observer. */
  handoffMs?: number;
  maxBytes?: number;
  maxEntries?: number;
}

type CachedQuery = Query;
interface Entry {
  accounted: boolean;
  data: unknown;
  handoffUntil: number;
  present: boolean;
  query: CachedQuery;
}

const OBJECT_BYTES = 80;
// Allow for the estimator's weak ownership entry as well as the payload itself.
const OWNERSHIP_BYTES = 32;
const QUERY_BYTES = 4096;
const MAX_STEPS_PER_SLICE = 4096;
const SLICE_MS = 2;

function primitiveBytes(value: unknown): number {
  return typeof value === "string" ? 24 + value.length * 2 : 8;
}

function* ownEnumerableValues(value: object): Generator<unknown> {
  // Enumerate without building a second array/object containing every property.
  // Descriptors deliberately skip getters (and never call toJSON).
  for (const key in value) {
    if (Object.hasOwn(value, key)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      yield descriptor && "value" in descriptor ? descriptor.value : undefined;
    }
  }
}

function* mapValues(value: Map<unknown, unknown>): Generator<unknown> {
  for (const [key, item] of Map.prototype.entries.call(value)) {
    yield key;
    yield item;
  }
}

function* oneValue(value: unknown): Generator<unknown> {
  yield value;
}

interface Frame {
  charge: boolean;
  slotBytes: number;
  values: Iterator<unknown>;
}

function describeObject(value: object): { bytes: number; frame?: Omit<Frame, "charge"> } {
  if (typeof value === "function") {
    // Function closure environments and native allocations are not inspectable here.
    return { bytes: OBJECT_BYTES };
  }
  if (Array.isArray(value)) {
    return {
      bytes: OBJECT_BYTES + value.length * 8,
      frame: { slotBytes: 0, values: ownEnumerableValues(value) },
    };
  }
  if (value instanceof Map) {
    const size = Object.getOwnPropertyDescriptor(Map.prototype, "size")?.get?.call(value);
    return {
      bytes: OBJECT_BYTES + Number(size) * 40,
      frame: { slotBytes: 0, values: mapValues(value) },
    };
  }
  if (value instanceof Set) {
    const size = Object.getOwnPropertyDescriptor(Set.prototype, "size")?.get?.call(value);
    return {
      bytes: OBJECT_BYTES + Number(size) * 32,
      frame: { slotBytes: 0, values: Set.prototype.values.call(value) },
    };
  }
  if (value instanceof ArrayBuffer) {
    const byteLength = Object.getOwnPropertyDescriptor(
      ArrayBuffer.prototype,
      "byteLength",
    )?.get?.call(value);
    return { bytes: OBJECT_BYTES + Number(byteLength) };
  }
  if (ArrayBuffer.isView(value)) {
    const prototype =
      value instanceof DataView ? DataView.prototype : Object.getPrototypeOf(Uint8Array.prototype);
    const buffer = Object.getOwnPropertyDescriptor(prototype, "buffer")?.get?.call(value);
    return {
      bytes: OBJECT_BYTES,
      frame: { slotBytes: 0, values: oneValue(buffer) },
    };
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype === null || prototype === Object.prototype) {
    return {
      bytes: OBJECT_BYTES,
      frame: { slotBytes: 8, values: ownEnumerableValues(value) },
    };
  }
  // Do not follow database handles, QueryClients, React internals or other class instances.
  return { bytes: OBJECT_BYTES };
}

/**
 * Incrementally account one immutable query-data snapshot. Each object is counted once per root,
 * and its bytes once across all roots. Ownership counts are weak: there is no permanent per-query
 * list of every row, and shared rankings are not multiplied by the number of pages using them.
 */
function* changeOwnership(
  root: unknown,
  owners: WeakMap<object, number>,
  direction: 1 | -1,
): Generator<number> {
  const seen = new WeakSet<object>();
  const frames: Frame[] = [{ charge: true, slotBytes: 0, values: oneValue(root) }];
  while (frames.length > 0) {
    const frame = frames.at(-1);
    if (!frame) {
      break;
    }
    let next: IteratorResult<unknown>;
    try {
      next = frame.values.next();
    } catch {
      // An opaque/revoked Proxy must not make a background accounting task crash the app.
      frames.pop();
      yield 0;
      continue;
    }
    if (next.done) {
      frames.pop();
      yield 0;
      continue;
    }
    const value: unknown = next.value;
    yield frame.charge ? frame.slotBytes * direction : 0;
    if (typeof value !== "function" && (typeof value !== "object" || value === null)) {
      yield frame.charge ? primitiveBytes(value) * direction : 0;
      continue;
    }
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    const before = owners.get(value) ?? 0;
    const after = before + direction;
    if (after === 0) {
      owners.delete(value);
    } else {
      owners.set(value, after);
    }
    const charge = direction === 1 ? before === 0 : after === 0;
    let described: ReturnType<typeof describeObject>;
    try {
      described = describeObject(value);
    } catch {
      described = { bytes: OBJECT_BYTES };
    }
    yield charge ? (described.bytes + OWNERSHIP_BYTES) * direction : 0;
    if (described.frame) {
      frames.push({ ...described.frame, charge });
    }
  }
}

/** Spatial companion to TanStack's time-based GC. All work is owned by this cache/client. */
export class InactiveQueryMemoryBudget {
  readonly #client: QueryClient;
  readonly #maxEntries: number;
  readonly #maxBytes: number;
  readonly #handoffMs: number;
  readonly #entries = new Map<CachedQuery, Entry>();
  readonly #dirty = new Set<Entry>();
  readonly #cleanup = new Set<Entry>();
  #accountedEntries = 0;
  #owners = new WeakMap<object, number>();
  #bytes = 0;
  #work: Generator<number> | undefined;
  #workingEntry: Entry | undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #wakeAt = Infinity;
  #disposed = false;
  #evictions = 0;
  readonly #unsubscribe: () => void;

  constructor(client: QueryClient, options: InactiveQueryMemoryBudgetOptions = {}) {
    this.#client = client;
    this.#maxEntries = options.maxEntries ?? 512;
    this.#maxBytes = options.maxBytes ?? 16 * 1024 * 1024;
    this.#handoffMs = options.handoffMs ?? 2000;
    if (this.#maxEntries < 0 || this.#maxBytes < 0 || this.#handoffMs < 0) {
      throw new Error("Inactive query budgets and handoff time must be non-negative");
    }
    for (const query of client.getQueryCache().getAll()) {
      this.#add(query);
    }
    this.#unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type === "added") {
        this.#add(event.query);
        return;
      }
      const entry = this.#entries.get(event.query);
      if (!entry) {
        return;
      }
      if (event.type === "removed") {
        entry.present = false;
        this.#entries.delete(event.query);
        this.#dirty.delete(entry);
        this.#cleanup.add(entry);
        if (this.#entries.size === 0) {
          this.#resetAccounting();
        } else {
          this.#schedule();
        }
        return;
      }
      // Initial optimistic results can be read during render before an observer subscribes.
      // Renew their handoff, but don't walk unchanged data for result/options notifications.
      if (event.type === "observerResultsUpdated" || event.type === "observerOptionsUpdated") {
        this.touch(event.query);
        return;
      }
      if (
        event.type === "observerAdded" ||
        event.type === "observerRemoved" ||
        (event.type === "updated" &&
          (event.action.type === "success" ||
            event.action.type === "error" ||
            event.action.type === "fetch"))
      ) {
        this.touch(event.query);
      }
      this.#queue(entry);
    });
  }

  /** Semantic reads must touch too: fresh imperative cache hits emit no QueryCache event. */
  touch(query: CachedQuery | undefined): void {
    const entry = query && this.#entries.get(query);
    if (!entry || this.#disposed) {
      return;
    }
    entry.handoffUntil = Date.now() + this.#handoffMs;
    this.#entries.delete(entry.query);
    this.#entries.set(entry.query, entry);
    this.#schedule();
  }

  getStats(): { entries: number; estimatedBytes: number; evictions: number; pending: boolean } {
    return {
      entries: this.#entries.size,
      estimatedBytes: Math.max(0, this.#bytes),
      evictions: this.#evictions,
      pending: this.#work !== undefined || this.#dirty.size > 0 || this.#cleanup.size > 0,
    };
  }

  dispose(): void {
    this.#disposed = true;
    this.#unsubscribe();
    this.#entries.clear();
    this.#resetAccounting();
  }

  #resetAccounting(): void {
    if (this.#timer !== undefined) {
      clearTimeout(this.#timer);
    }
    this.#timer = undefined;
    this.#wakeAt = Infinity;
    this.#work = undefined;
    this.#workingEntry = undefined;
    this.#dirty.clear();
    this.#cleanup.clear();
    this.#accountedEntries = 0;
    this.#owners = new WeakMap();
    this.#bytes = 0;
  }

  #add(query: CachedQuery): void {
    const entry: Entry = {
      accounted: false,
      data: undefined,
      handoffUntil: Date.now() + this.#handoffMs,
      present: true,
      query,
    };
    this.#entries.set(query, entry);
    this.#queue(entry);
  }

  #track(entry: Entry): boolean {
    return (
      entry.present &&
      entry.query.getObserversCount() === 0 &&
      entry.query.state.fetchStatus === "idle"
    );
  }

  #queue(entry: Entry): void {
    if (entry.accounted && (!this.#track(entry) || entry.data !== entry.query.state.data)) {
      this.#cleanup.add(entry);
    } else {
      this.#dirty.add(entry);
    }
    this.#schedule();
  }

  #schedule(delay = 0): void {
    if (this.#disposed) {
      return;
    }
    const wakeAt = Date.now() + delay;
    if (this.#timer !== undefined && this.#wakeAt <= wakeAt) {
      return;
    }
    if (this.#timer !== undefined) {
      clearTimeout(this.#timer);
    }
    this.#wakeAt = wakeAt;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#wakeAt = Infinity;
      this.#runSlice();
    }, delay);
  }

  #startChange(entry: Entry, direction: 1 | -1): void {
    this.#workingEntry = entry;
    this.#accountedEntries += direction;
    if (direction === 1) {
      entry.accounted = true;
      entry.data = entry.query.state.data;
    }
    this.#bytes += (QUERY_BYTES + entry.query.queryHash.length * 2) * direction;
    this.#work = changeOwnership(entry.data, this.#owners, direction);
    if (direction === -1) {
      entry.accounted = false;
      // The generator owns this one obsolete snapshot only until its bounded cleanup completes.
      entry.data = undefined;
    }
  }

  #nextWork(): boolean {
    // Release obsolete/now-observed snapshots before evicting anything for byte pressure.
    for (const entry of this.#cleanup) {
      this.#cleanup.delete(entry);
      if (entry.accounted) {
        this.#startChange(entry, -1);
        return true;
      }
    }
    if (this.#evictIfNeeded()) {
      return true;
    }
    for (const entry of this.#dirty) {
      this.#dirty.delete(entry);
      if (this.#track(entry) && !entry.accounted) {
        this.#startChange(entry, 1);
        return true;
      }
    }
    return false;
  }

  #evictIfNeeded(): boolean {
    let count = 0;
    let oldest: Entry | undefined;
    let wakeAt = Infinity;
    const now = Date.now();
    for (const entry of this.#entries.values()) {
      if (!this.#track(entry)) {
        continue;
      }
      count++;
      if (entry.handoffUntil > now) {
        wakeAt = Math.min(wakeAt, entry.handoffUntil);
      } else if (entry.query.promise === undefined) {
        oldest ??= entry;
      }
    }
    if (count <= this.#maxEntries && this.#bytes <= this.#maxBytes) {
      return false;
    }
    if (oldest) {
      const cache = this.#client.getQueryCache();
      // Removal destroys/cancels a Query, so check the actual instance and every protection again.
      if (
        cache.get(oldest.query.queryHash) === oldest.query &&
        this.#track(oldest) &&
        oldest.query.promise === undefined &&
        oldest.handoffUntil <= Date.now()
      ) {
        this.#evictions++;
        cache.remove(oldest.query);
        return true;
      }
    }
    if (wakeAt !== Infinity) {
      this.#schedule(Math.max(1, wakeAt - now));
    }
    return false;
  }

  #runSlice(): void {
    const started = performance.now();
    for (let step = 0; step < MAX_STEPS_PER_SLICE; step++) {
      if (this.#work) {
        const next = this.#work.next();
        if (next.done) {
          const entry = this.#workingEntry;
          this.#work = undefined;
          this.#workingEntry = undefined;
          if (this.#accountedEntries === 0) {
            // Hermes deletes WeakMap keys without shrinking its peak native bucket allocation.
            // Only retire the empty table after the last removal walk finishes: resetting it
            // while a shared snapshot or a suspended cleanup remains would lose reference counts.
            this.#owners = new WeakMap();
          }
          // A new result, observation, cancellation or removal may have arrived during the walk.
          if (entry) {
            this.#queue(entry);
          }
        } else {
          this.#bytes += next.value;
        }
      } else if (!this.#nextWork()) {
        return;
      }
      if ((step & 31) === 31 && performance.now() - started >= SLICE_MS) {
        break;
      }
    }
    this.#schedule();
  }
}
